/** Authentication contracts shared by the API and both front ends. */
import { z } from 'zod';
import { userRoleSchema } from './enums.js';
import { emailSchema } from './primitives.js';
export const loginSchema = z.object({
    email: emailSchema,
    password: z.string().min(1, 'Enter your password').max(200),
});
/**
 * Length over composition rules. NIST withdrew the mixed-character-class advice years
 * ago; a 12-character passphrase beats `P@ssw0rd!` and users stop writing them down.
 */
export const passwordSchema = z
    .string()
    .min(12, 'Use at least 12 characters — a short phrase works well')
    .max(200)
    .refine((v) => v.trim().length === v.length, {
    message: 'Remove the leading or trailing space',
});
/** Asking for a reset. The response never varies, so neither does this. */
export const forgotPasswordSchema = z.object({ email: emailSchema });
export const setPasswordSchema = z
    .object({
    token: z.string().min(10),
    password: passwordSchema,
    confirmPassword: z.string(),
})
    .refine((v) => v.password === v.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Those two passwords do not match',
});
export const changePasswordSchema = z
    .object({
    currentPassword: z.string().min(1),
    newPassword: passwordSchema,
    confirmPassword: z.string(),
})
    .refine((v) => v.newPassword === v.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Those two passwords do not match',
})
    .refine((v) => v.newPassword !== v.currentPassword, {
    path: ['newPassword'],
    message: 'Choose a password you have not used here before',
});
export const requestPasswordResetSchema = z.object({ email: emailSchema });
export const sessionUserSchema = z.object({
    id: z.string(),
    email: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    role: userRoleSchema,
    chamberId: z.string().nullable(),
    organisationId: z.string().nullable(),
    mustChangePassword: z.boolean(),
    /** Everything needed to theme the shell before the first data request returns. */
    chamber: z
        .object({
        id: z.string(),
        slug: z.string(),
        legalName: z.string(),
        shortName: z.string(),
        logoUrl: z.string().nullable(),
        primaryColor: z.string(),
        secondaryColor: z.string(),
    })
        .nullable(),
});
export const loginResponseSchema = z.object({
    accessToken: z.string(),
    refreshToken: z.string(),
    expiresIn: z.number().int(),
    user: sessionUserSchema,
});
/**
 * A console left untouched this long signs out (T-140). Counted from keyboard, mouse and
 * touch in any tab — never from background polling, which would keep an unattended desk
 * signed in forever.
 */
export const SESSION_IDLE_MINUTES = 30;
/** How long before the idle sign-out the console warns, so a click can keep the session. */
export const SESSION_IDLE_WARNING_SECONDS = 120;
//# sourceMappingURL=auth.js.map